// src/features/tidsregistering/components/attendance/AttendanceChart.jsx
import React, { useCallback, useState, useEffect } from 'react';
import { Paper, Box, useTheme } from '@mui/material';
import { TimelineGrid, ROW_NAME_WIDTH } from '../../../../components/timeline';
import { GanttDndProvider } from '../../../../components/gantt/context/GanttDndContext';
import { useHourViewLogic } from './useHourViewLogic';
import HourViewHeader, { HOUR_VIEW_HEADER_HEIGHT } from './HourViewHeader';
import AttendanceRow, { ATTENDANCE_ROW_HEIGHT } from './AttendanceRow';
import AttendanceSidebarCell, { GROUP_HEADER_HEIGHT } from './AttendanceSidebarCell';
import { useTranslation } from 'react-i18next';

const getAttendanceCategoryHeight = (cat) => cat.isGroupHeader ? GROUP_HEADER_HEIGHT : ATTENDANCE_ROW_HEIGHT;

function useNow() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/**
 * AttendanceChart — hour-granularity timeline for a single day.
 *
 * Reuses the shared TimelineGrid infrastructure via:
 *   renderHeader → HourViewHeader (hours 00–23 with 4 sub-columns each)
 *   renderRows   → AttendanceRow (time blocks + schedule background)
 *   renderLabel  → AttendanceSidebarCell (staff name + deviation badge)
 *
 * Props:
 *   categories   — Category[] from dayOverviewsToCategories adapter
 *   selectedDate — string 'yyyy-MM-dd'
 *   onSlotClick  — (slot) => void
 *   dayStartHour — number (default 0)
 *   dayEndHour   — number (default 24)
 *   slotMinutes  — number (default 15, gives 4 sub-columns per hour)
 *   slotWidthPx  — number px per 15-min column (default 30)
 *   height       — CSS height string (default '70vh')
 */
export default function AttendanceChart({
  categories = [],
  selectedDate,
  onSlotClick = null,
  onDragCreate = null,
  onShiftDragCreate = null,
  balancesMap = {},
  onFetchBalances = null,
  dayStartHour = 0,
  dayEndHour = 24,
  slotMinutes = 15,
  slotWidthPx = 15,
  height = '70vh',
  unavailabilityBlocks = [],
}) {
  const { t } = useTranslation(['common']);
  const now = useNow();
  const theme = useTheme();

  const {
    scrollContainerRef,
    scrollState,
    handleScroll,
    baseDate,
    timeToX,
    slotIndexToTime,
    hourGroups,
    SLOT_WIDTH,
    SLOT_MINUTES,
    totalSlots,
  } = useHourViewLogic({ selectedDate, dayStartHour, dayEndHour, slotMinutes, slotWidthPx });

  const renderHeader = useCallback(
    ({ visibleDayIndices, dayWidth }) => (
      <HourViewHeader
        visibleDayIndices={visibleDayIndices}
        dayWidth={dayWidth}
        hourGroups={hourGroups}
        slotMinutes={SLOT_MINUTES}
        totalSlots={totalSlots}
      />
    ),
    [hourGroups, SLOT_MINUTES, totalSlots],
  );

  const renderRows = useCallback(
    ({ categories: cats, visibleDayIndices, dayWidth, contentOffset = 0 }) => {
      const adjustedTimeToX = (date) => timeToX(date) - contentOffset;
      const nowX = adjustedTimeToX(now);
      const totalRowsHeight = cats.reduce(
        (sum, cat) => sum + getAttendanceCategoryHeight(cat),
        0,
      );

      return (
        <>
          {cats.map((category) => {
            if (category.isGroupHeader) {
              return (
                <Box
                  key={category.id}
                  sx={{
                    height: GROUP_HEADER_HEIGHT,
                    bgcolor: (theme) => `${theme.palette.primary.main}14`,
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                />
              );
            }
            return (
              <AttendanceRow
                key={category.id}
                category={category}
                timeToX={adjustedTimeToX}
                visibleDayIndices={visibleDayIndices}
                dayWidth={dayWidth}
                slotIndexToTime={slotIndexToTime}
                onSlotClick={onSlotClick}
                onDragCreate={onDragCreate}
                onShiftDragCreate={onShiftDragCreate}
                unavailabilityBlocks={unavailabilityBlocks.filter((b) => b.staff_member_id === category.id)}
                selectedDate={selectedDate}
              />
            );
          })}
          {/* Current-time indicator — updates every minute */}
          {nowX >= 0 && (
            <Box
              sx={{
                position: 'absolute',
                left: nowX,
                top: 0,
                width: 2,
                height: totalRowsHeight,
                bgcolor: theme.palette.error.main,
                opacity: 0.7,
                pointerEvents: 'none',
                zIndex: 10,
              }}
            />
          )}
        </>
      );
    },
    [timeToX, now, theme, slotIndexToTime, onSlotClick, onDragCreate, onShiftDragCreate, unavailabilityBlocks, selectedDate],
  );

  const renderLabel = useCallback(
    ({ category, categoryHeight }) => (
      <AttendanceSidebarCell
        key={category.id}
        category={category}
        categoryHeight={categoryHeight}
        balances={balancesMap[category.id] ?? undefined}
        onFetchBalances={onFetchBalances}
      />
    ),
    [balancesMap, onFetchBalances],
  );

  return (
    <GanttDndProvider>
      <Paper sx={{ height, display: 'flex', overflow: 'hidden' }}>
        <TimelineGrid
          ref={scrollContainerRef}
          sidebarTitle={t('common:navigation.staff_members')}
          onScroll={handleScroll}
          scrollState={scrollState}
          categories={categories}
          baseDate={baseDate}
          dayWidth={SLOT_WIDTH}
          rowNameWidth={ROW_NAME_WIDTH}
          renderHeader={renderHeader}
          headerHeight={HOUR_VIEW_HEADER_HEIGHT}
          renderRows={renderRows}
          renderLabel={renderLabel}
          getCategoryRowHeight={getAttendanceCategoryHeight}
          visibleDaysOverride={totalSlots - 1}
          showBaseDateIndicator={false}
        />
      </Paper>
    </GanttDndProvider>
  );
}
